import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Store, Truck } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/apiClient";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const TIME_SLOTS = ["10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM"];
const UNIT_MAP = { single: 1, bundle3: 3, bundle6: 6, bundle12: 12, item: 1 };

const computeShipping = (shipping, units) => {
  if (!shipping || !shipping.enabled) return 0;
  if (shipping.mode === "tiered") {
    const tiers = [...(shipping.tiers || [])].sort((a, b) => a.max_units - b.max_units);
    for (const t of tiers) if (units <= t.max_units) return Number(t.price);
    return tiers.length ? Number(tiers[tiers.length - 1].price) : 0;
  }
  return Number(shipping.flat_rate || 0);
};

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [shipping, setShipping] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [fulfillment, setFulfillment] = useState("pickup");
  const [form, setForm] = useState({
    name: "", phone: "", email: "", pickup_date: "", pickup_time: "",
    address_street: "", address_city: "", address_state: "", address_zip: "", notes: "",
  });
  const orderIdRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    api.get("/settings").then((res) => {
      setShipping(res.data.shipping);
      setPaypalClientId(res.data.paypal_client_id || null);
    }).catch(() => {});
  }, []);

  const units = useMemo(() => items.reduce((sum, i) => sum + (UNIT_MAP[i.product_type] || 1), 0), [items]);
  const shippingCost = fulfillment === "shipping" ? computeShipping(shipping, units) : 0;
  const total = subtotal + shippingCost;
  const shippingEnabled = shipping && shipping.enabled;

  const formValid =
    form.name.trim() && form.phone.trim() &&
    (fulfillment === "pickup"
      ? form.pickup_date && form.pickup_time
      : form.address_street && form.address_city && form.address_state && form.address_zip);

  const buildPayload = () => ({
    items: items.map((i) => ({ product_type: i.product_type, item_id: i.item_id || null, flavors: (i.flavors || []).map((f) => f.id) })),
    customer: form,
    fulfillment,
    origin_url: window.location.origin,
  });

  const createOrder = async () => {
    const res = await api.post("/checkout/create-order", buildPayload());
    orderIdRef.current = res.data.order_id;
    return res.data.paypal_order_id;
  };

  const onApprove = async (data) => {
    try {
      const res = await api.post("/checkout/capture-order", { paypal_order_id: data.orderID });
      if (res.data.status === "COMPLETED") {
        clearCart();
        navigate(`/checkout/success?order_id=${res.data.order_id}`);
      } else {
        toast.error("Payment was not completed. Please try again.");
      }
    } catch (e) {
      toast.error("We couldn't confirm your payment. Please contact us if you were charged.");
    }
  };

  const methodBtn = (value, Icon, title, sub) => (
    <button
      data-testid={`fulfillment-${value}`}
      onClick={() => setFulfillment(value)}
      className={`flex flex-1 items-center gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${
        fulfillment === value ? "border-cocoa bg-babybluelight/60" : "border-ashy/30 bg-white hover:border-cocoa/40"
      }`}
    >
      <Icon className={`h-6 w-6 ${fulfillment === value ? "text-cocoa" : "text-ashydark"}`} />
      <div>
        <p className="font-semibold text-ink">{title}</p>
        <p className="text-xs text-ashydark">{sub}</p>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      <div className="mx-auto max-w-5xl px-6 py-12 md:px-12">
        <button onClick={() => navigate("/")} data-testid="back-to-shop-button" className="mb-6 flex items-center gap-2 text-sm text-ashydark transition-colors hover:text-cocoa">
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </button>
        <h1 className="mb-8 font-heading text-4xl font-black text-ink sm:text-5xl">Checkout</h1>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-ashy/30 bg-white py-20 text-center">
            <ShoppingBag className="mb-4 h-12 w-12 text-ashy" />
            <p className="text-ashydark">Your cart is empty.</p>
            <Button onClick={() => navigate("/")} className="mt-4 rounded-full bg-cocoa text-white hover:bg-cocoadark">Browse cookies</Button>
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <div className="rounded-3xl border border-ashy/30 bg-white p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold text-ink">How would you like it?</h2>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  {methodBtn("pickup", Store, "Pickup", "Free · pick a date & time")}
                  {shippingEnabled && methodBtn("shipping", Truck, shipping.label || "Shipping", shipping.mode === "flat" ? `$${Number(shipping.flat_rate).toFixed(2)} flat` : "Rate by order size")}
                </div>

                <h2 className="mt-8 font-heading text-2xl font-bold text-ink">Your Details</h2>
                <div className="mt-5 space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input id="name" data-testid="checkout-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5" placeholder="Jane Doe" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input id="phone" data-testid="checkout-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5" placeholder="(555) 123-4567" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email (optional)</Label>
                    <Input id="email" data-testid="checkout-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5" placeholder="you@email.com" />
                  </div>

                  {fulfillment === "pickup" ? (
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="date">Pickup Date *</Label>
                        <Input id="date" type="date" min={today} data-testid="checkout-date" value={form.pickup_date} onChange={(e) => update("pickup_date", e.target.value)} className="mt-1.5" />
                      </div>
                      <div>
                        <Label htmlFor="time">Pickup Time *</Label>
                        <select id="time" data-testid="checkout-time" value={form.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm">
                          <option value="">Select a time</option>
                          {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-5" data-testid="shipping-address-fields">
                      <div>
                        <Label htmlFor="street">Street Address *</Label>
                        <Input id="street" data-testid="checkout-street" value={form.address_street} onChange={(e) => update("address_street", e.target.value)} className="mt-1.5" placeholder="123 Main St, Apt 4" />
                      </div>
                      <div className="grid gap-5 sm:grid-cols-3">
                        <div>
                          <Label htmlFor="city">City *</Label>
                          <Input id="city" data-testid="checkout-city" value={form.address_city} onChange={(e) => update("address_city", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                          <Label htmlFor="state">State *</Label>
                          <Input id="state" data-testid="checkout-state" value={form.address_state} onChange={(e) => update("address_state", e.target.value)} className="mt-1.5" placeholder="PA" />
                        </div>
                        <div>
                          <Label htmlFor="zip">ZIP *</Label>
                          <Input id="zip" data-testid="checkout-zip" value={form.address_zip} onChange={(e) => update("address_zip", e.target.value)} className="mt-1.5" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea id="notes" data-testid="checkout-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} className="mt-1.5" placeholder="Any special requests?" />
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="lg:col-span-2">
              <div className="sticky top-24 rounded-3xl border border-ashy/30 bg-white p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold text-ink">Order Summary</h2>
                <div className="mt-5 space-y-4">
                  {items.map((item) => (
                    <div key={item.lineId} className="flex justify-between gap-3 border-b border-ashy/20 pb-3 text-sm">
                      <div>
                        <p className="font-semibold text-ink">{item.label}</p>
                        {item.flavors && item.flavors.length > 0 && <p className="text-ashydark">{item.flavors.map((f) => f.name).join(", ")}</p>}
                      </div>
                      <span className="font-semibold text-cocoa">${item.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between text-ashydark">
                    <span>Subtotal</span>
                    <span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-ashydark">
                    <span>{fulfillment === "shipping" ? (shipping?.label || "Shipping") : "Pickup"}</span>
                    <span data-testid="checkout-shipping">{fulfillment === "shipping" ? `$${shippingCost.toFixed(2)}` : "Free"}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-ashy/20 pt-3">
                  <span className="text-ashydark">Total</span>
                  <span data-testid="checkout-total" className="font-heading text-3xl font-black text-ink">${total.toFixed(2)}</span>
                </div>

                {!formValid && (
                  <p className="mt-4 rounded-lg bg-babybluelight/60 px-3 py-2 text-center text-xs text-ashydark" data-testid="checkout-fill-hint">
                    Fill in your details above to pay.
                  </p>
                )}

                <div className="mt-4" data-testid="paypal-buttons-container">
                  {paypalClientId ? (
                    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "USD", intent: "capture", "enable-funding": "venmo" }}>
                      <div style={{ opacity: formValid ? 1 : 0.5, pointerEvents: formValid ? "auto" : "none" }}>
                        <PayPalButtons
                          style={{ layout: "vertical", shape: "pill", color: "gold" }}
                          disabled={!formValid}
                          forceReRender={[total, fulfillment, formValid]}
                          createOrder={createOrder}
                          onApprove={onApprove}
                          onError={() => toast.error("Something went wrong with PayPal. Please try again.")}
                        />
                      </div>
                    </PayPalScriptProvider>
                  ) : (
                    <p className="text-center text-sm text-ashydark">Loading payment options…</p>
                  )}
                </div>
                <p className="mt-2 text-center text-xs text-ashydark">Pay securely with PayPal or Venmo</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
